import type { FullOutput } from '@mastra/core/stream';

export function extractOpenRouterGenerationId(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const match = value.match(OPENROUTER_GENERATION_ID_PATTERN);
    return match?.[0];
  }

  if (value && typeof value === 'object') {
    try {
      const serialized = JSON.stringify(value);
      const match = serialized.match(OPENROUTER_GENERATION_ID_PATTERN);
      return match?.[0];
    } catch {
      return;
    }
  }

  return;
}

export function resolveOpenRouterGenerationId(
  output: FullOutput<unknown>,
  responseId?: string | undefined,
): string | undefined {
  const outputRecord = output;
  const responseRecord = asRecord(outputRecord.response);
  const rawResponse = asRecord('rawResponse' in outputRecord ? outputRecord.rawResponse : undefined);
  const providerMetadata = output.providerMetadata;
  const openRouterMetadata = asRecord(providerMetadata?.openrouter);
  const openRouterGeneration = asRecord(openRouterMetadata?.generation);

  const candidates = [
    responseId,
    responseRecord?.id,
    openRouterMetadata?.generationId,
    openRouterMetadata?.generation_id,
    openRouterMetadata?.id,
    openRouterGeneration?.id,
    rawResponse?.body,
    rawResponse?.headers,
    openRouterMetadata,
    'reasoningDetails' in outputRecord ? outputRecord.reasoningDetails : undefined,
    outputRecord.reasoning,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const generationId = extractOpenRouterGenerationId(candidate);
    if (generationId) return generationId;
  }

  return;
}

export const OPENROUTER_GENERATION_ID_PATTERN = /\bgen-[0-9]{10}-[A-Za-z0-9_-]+\b/;

export function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
}

export function hasNonEmptyTrimmedString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function toNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return;
  const trimmed = value.trim();
  if (trimmed.length === 0) return;
  return trimmed;
}
export function normalizedString(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  const str = String(value).trim();
  return str.length > 0 ? str : undefined;
}

export function stripKnownProviderPrefix(modelName: string): string {
  const knownPrefixes = ['google/', 'openai/', 'anthropic/', 'openrouter/'] as const;
  const normalized = modelName.toLowerCase();

  for (const prefix of knownPrefixes) {
    if (normalized.startsWith(prefix)) {
      return modelName.slice(prefix.length);
    }
  }

  return modelName;
}
